import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { TreeSelection } from '../../next/CustomDash/filters/types/filters';

export type GeographyMode = 'msa' | 'administrative';

/**
 * Color theme preference (only meaningful when the DashboardNewLayout flag is
 * ON). 'system' follows the OS `prefers-color-scheme`; 'light'/'dark' force the
 * respective theme; 'legacy' removes the data-theme marker so components render
 * with the pre-redesign styling.
 */
export const COLOR_MODE_PREFERENCES = [
  'system',
  'light',
  'dark',
  'legacy',
] as const;

export type ColorModePreference = (typeof COLOR_MODE_PREFERENCES)[number];

export function isColorModePreference(
  value: unknown
): value is ColorModePreference {
  return COLOR_MODE_PREFERENCES.includes(value as ColorModePreference);
}

// Duplicated values to avoid circular dependency with filter types
export const UserPreferenceControlledFilterIds: ReadonlyArray<string> = [
  'CONTINGENT_WORKERS',
  'DATA_ADJUSTMENTS',
  'ROLE_TAXONOMY',
  'SKILL_TAXONOMY',
];

export enum PreferenceScope {
  GLOBAL = 'GLOBAL',
  INDIVIDUAL = 'INDIVIDUAL',
  COMPANY = 'COMPANY',
  GEOGRAPHY = 'GEOGRAPHY',
  ROLE = 'ROLE',
}

export enum UserPreferenceKey {
  ContingentWorkers = 'contingentWorkers',
  Taxonomies = 'taxonomies',
  DataAdjustments = 'dataAdjustments',
  DefaultFilters = 'defaultFilters',
  Currency = 'currency',
  Geography = 'geography',
  PageOrder = 'pageOrder',
  ColorMode = 'colorMode',
}

export enum UserPreferenceSubKey {
  RoleTaxonomy = 'roleTaxonomy',
  SkillTaxonomy = 'skillTaxonomy',
}

export type UserPreferences = {
  [UserPreferenceKey.ContingentWorkers]: boolean;
  [UserPreferenceKey.Taxonomies]: {
    role?: 'adaptive' | CustomRoleTaxonomySelection;
    effectiveRoleTaxonomySelection?: CustomRoleTaxonomySelection | null;
    /** Role taxonomy version the saved DefaultFilters were captured under. */
    roleTaxonomyVersion?: string;
    skill?: 'adaptive' | number;
    effectiveSkillTaxonomySelection?: number | null;
    /** Stored for symmetry with role; no consumer yet. */
    skillTaxonomyVersion?: string;
  };
  /**
   * Whether to use raw (unadjusted) data.
   * false => Adjusted (default)
   * true  => Unadjusted (raw)
   */
  [UserPreferenceKey.DataAdjustments]: boolean;
  [UserPreferenceKey.Currency]: {
    id?: string;
    code: string;
    rate: number;
  };
  [UserPreferenceKey.DefaultFilters]: TreeSelection[];
  [UserPreferenceKey.Geography]: GeographyMode;
  [UserPreferenceKey.PageOrder]: string[];
  [UserPreferenceKey.ColorMode]: ColorModePreference;
};

export type GlobalUserPreferences = Pick<
  UserPreferences,
  | UserPreferenceKey.Currency
  | UserPreferenceKey.Geography
  | UserPreferenceKey.ColorMode
>;

export type ScopedUserPreferences = Omit<
  UserPreferences,
  keyof GlobalUserPreferences
>;

/** List of keys that are considered global-only */
export const GLOBAL_PREFERENCE_KEYS: (keyof UserPreferences)[] = [
  UserPreferenceKey.Currency,
  UserPreferenceKey.Geography,
  UserPreferenceKey.ColorMode,
];

/**
 * Preferences that manage their own persistence (immediate write, own reset
 * UX) and should NOT participate in the modal's draft/diff/bulk-restore flow.
 * Excluded from `calculateVisiblePreferenceDiff`, `saveVisiblePreferencesAsDefault`,
 * and `restoreVisiblePreferencesToDefaults`.
 */
export const SELF_MANAGED_PREFERENCE_KEYS: UserPreferenceKey[] = [
  UserPreferenceKey.PageOrder,
];

/** Scopes where `PageOrder` is editable / cleaned-up. */
export const PAGE_ORDER_SCOPES: PreferenceScope[] = [
  PreferenceScope.COMPANY,
  PreferenceScope.GEOGRAPHY,
  PreferenceScope.ROLE,
];
